import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Users, Search, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUsers(page);
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await adminAPI.updateUser(userId, data);
      toast.success('User updated');
      fetchUsers();
      setEditingUser(null);
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Users Management
          </h1>
          <p className="text-[#5F6368]">Manage all registered users</p>
        </div>
      </div>

      <div className="dashboard-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#DADCE0]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">User</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Plan</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Verified</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Active</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Auto-Post</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Auto-Reply</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Admin</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#5F6368]">Joined</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[#5F6368]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-[#DADCE0] hover:bg-[#F8F9FA]">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-[#202124]">{user.name}</p>
                          <p className="text-sm text-[#5F6368]">{user.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {editingUser === user.id ? (
                          <select
                            defaultValue={user.membership_plan}
                            onChange={(e) => handleUpdateUser(user.id, { membership_plan: e.target.value })}
                            className="input-google py-1 px-2 text-sm"
                          >
                            <option value="free">Free</option>
                            <option value="basic">Basic</option>
                            <option value="pro">Pro</option>
                          </select>
                        ) : (
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            user.membership_plan === 'pro' ? 'bg-[#F3E8FD] text-[#9334E9]' :
                            user.membership_plan === 'basic' ? 'bg-[#E8F0FE] text-[#1A73E8]' :
                            'bg-[#F1F3F4] text-[#5F6368]'
                          }`}>
                            {user.membership_plan?.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleUpdateUser(user.id, { email_verified: !user.email_verified })}
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            user.email_verified ? 'bg-[#E6F4EA] text-[#1E8E3E]' : 'bg-[#FEF7E0] text-[#E37400]'
                          }`}
                          data-testid={`verify-user-${user.id}`}
                        >
                          {user.email_verified ? 'Verified' : 'Pending'}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleUpdateUser(user.id, { is_active: user.is_active === false ? true : false })}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            user.is_active !== false ? 'bg-[#1E8E3E]' : 'bg-[#D93025]'
                          }`}
                          data-testid={`toggle-active-${user.id}`}
                        >
                          <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                            user.is_active !== false ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleUpdateUser(user.id, { auto_post_enabled: !user.auto_post_enabled })}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            user.auto_post_enabled ? 'bg-[#1A73E8]' : 'bg-[#DADCE0]'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                            user.auto_post_enabled ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleUpdateUser(user.id, { auto_reply_enabled: !user.auto_reply_enabled })}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            user.auto_reply_enabled ? 'bg-[#1E8E3E]' : 'bg-[#DADCE0]'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                            user.auto_reply_enabled ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleUpdateUser(user.id, { is_admin: !user.is_admin })}
                          className={`w-10 h-5 rounded-full transition-colors ${
                            user.is_admin ? 'bg-[#1A73E8]' : 'bg-[#DADCE0]'
                          }`}
                        >
                          <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${
                            user.is_admin ? 'translate-x-5' : 'translate-x-0.5'
                          }`} />
                        </button>
                      </td>
                      <td className="py-3 px-4 text-sm text-[#5F6368]">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => setEditingUser(editingUser === user.id ? null : user.id)}
                          className="p-2 text-[#5F6368] hover:text-[#1A73E8] hover:bg-[#E8F0FE] rounded-lg mr-1"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 text-[#5F6368] hover:text-[#D93025] hover:bg-[#FCE8E6] rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t border-[#DADCE0]">
              <p className="text-sm text-[#5F6368]">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 text-[#5F6368] hover:bg-[#F1F3F4] rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 text-[#5F6368] hover:bg-[#F1F3F4] rounded-lg disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
